// SPDX-License-Identifier: BUSL-1.1

/**
 * Badge components for status display.
 *
 */

import type { PiDevAgentStatus, PiDevSessionStatus, PolicyGate } from '@/types/api';
import {
  AGENT_HEALTH_LABELS,
  type AgentHealth,
  type AgentHealthTone,
  AGENT_HEALTH_TONE,
  deriveAgentHealth,
  type DeriveAgentHealthInput,
} from '@/lib/agent-health';
import { HEARTBEAT_INVOCATION_SOURCE_LABELS, type HeartbeatInvocationSource } from '@build-ceo/api';

/* ── Agent Status Badges ─────────────────────────── */

export function PiDevAgentStatusBadge({ status }: { status: PiDevAgentStatus }) {
  const colors: Record<PiDevAgentStatus, string> = {
    idle: 'bg-yellow-900/40 text-yellow-400',
    running: 'bg-green-900/30 text-green-400',
    paused: 'bg-blue-900/30 text-blue-400',
    terminated: 'bg-neutral-700 text-neutral-300',
  };

  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[status]}`}>{status}</span>
  );
}

export function PiDevSessionStatusBadge({ status }: { status: PiDevSessionStatus }) {
  const colors: Record<PiDevSessionStatus, string> = {
    active: 'bg-green-900/30 text-green-400',
    completed: 'bg-neutral-700 text-neutral-300',
    terminated: 'bg-red-900/30 text-red-400',
  };

  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[status]}`}>{status}</span>
  );
}

/* ── Policy Gate Badge ──────────────────────────── */

export function PolicyGateBadge({ gate }: { gate: PolicyGate }) {
  const colors: Record<PolicyGate, string> = {
    safe: 'bg-green-900/30 text-green-400',
    guarded: 'bg-yellow-900/40 text-yellow-400',
    manual: 'bg-red-900/30 text-red-400',
  };

  const labels: Record<PolicyGate, string> = {
    safe: 'Safe',
    guarded: 'Guarded',
    manual: 'Manual',
  };

  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${colors[gate]}`}>
      {labels[gate]}
    </span>
  );
}

/* ── Budget Usage Bar ────────────────────────────── */

export function BudgetUsageBar({
  used,
  limit,
  percentUsed,
}: {
  used: number;
  limit: number;
  percentUsed: number | null;
}) {
  if (!limit) {
    return <span className="text-neutral-500">Unlimited</span>;
  }

  const percent = percentUsed ?? (limit > 0 ? (used / limit) * 100 : 0);
  const color = percent > 90 ? 'bg-red-500' : percent > 75 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-neutral-800">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
      <span className="text-xs text-neutral-400">{percent.toFixed(0)}%</span>
    </div>
  );
}

/* ── Agent Health Badge (HB-02) ─────────────────── */

const AGENT_HEALTH_TONE_CLASSES: Record<AgentHealthTone, string> = {
  ok: 'bg-green-900/30 text-green-400',
  neutral: 'bg-blue-900/30 text-blue-400',
  warning: 'bg-amber-900/30 text-amber-400',
  muted: 'bg-neutral-700 text-neutral-300',
  error: 'bg-red-900/30 text-red-400',
};

export function AgentHealthBadge({ health }: { health: AgentHealth }) {
  const className = AGENT_HEALTH_TONE_CLASSES[AGENT_HEALTH_TONE[health]];
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs ${className}`}>
      {AGENT_HEALTH_LABELS[health]}
    </span>
  );
}

/**
 * Convenience wrapper that derives the health label from an agent's
 * (status, lastHeartbeat) and renders the appropriate badge. Use this
 * when you have the raw agent shape; use AgentHealthBadge directly when
 * the health was already computed upstream.
 */
export function DerivedAgentHealthBadge({ agent }: { agent: DeriveAgentHealthInput }) {
  return <AgentHealthBadge health={deriveAgentHealth(agent)} />;
}

/* ── Heartbeat Invocation Source Badge (HB-07) ───────── */

const HEARTBEAT_SOURCE_CLASSES: Record<HeartbeatInvocationSource, string> = {
  timer: 'bg-blue-900/30 text-blue-300',
  assignment: 'bg-purple-900/30 text-purple-300',
  on_demand: 'bg-amber-900/30 text-amber-300',
  automation: 'bg-cyan-900/30 text-cyan-300',
  unknown: 'bg-neutral-700 text-neutral-400',
};

export function HeartbeatSourceBadge({ source }: { source: HeartbeatInvocationSource }) {
  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 font-mono text-xs ${HEARTBEAT_SOURCE_CLASSES[source]}`}
    >
      {HEARTBEAT_INVOCATION_SOURCE_LABELS[source]}
    </span>
  );
}

// Repo badge (Sprint 42 S2-T2)
export function RepoBadge({ repo }: { repo: string }) {
  return (
    <span className="bg-muted inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium dark:bg-neutral-800 dark:text-neutral-300">
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
      </svg>
      {repo.split('/')[1] || repo}
    </span>
  );
}

// Stylepack badge (Sprint 42 S4-T2)
export function StylepackBadge({ name, version }: { name: string; version?: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
        />
      </svg>
      {name}
      {version && <span className="opacity-60">v{version}</span>}
    </span>
  );
}
